import { AppBar, makeStyles, Toolbar } from '@material-ui/core';
import {
  LOAD_STATUS,
  ON_BILLING_STATUS,
  ON_TIME_STATUS,
  ON_TIME_STATUS_TEXT,
} from 'doc-mate-store/lib/constants/load';
import { useStores } from 'doc-mate-store/lib/hooks';
import { observer } from 'mobx-react-lite';
import React from 'react';
import { onBillingStatusColors, onTimeStatusColors } from '../../themes';

import { EditableStatusSelect } from './EditableStatusSelect';
import { ReadOnlyStatusText } from './ReadOnlyStatusText';

type Props = {
  flex?: number;
  loadStatus: string;
  onTimeStatus: ON_TIME_STATUS;
  sticky?: boolean;
  loadId?: number;
  status?: LOAD_STATUS;
  billingIssueDescription?: string;
  onStatusUpdated?: () => void;
};

const getBillingStatusEnum = (statusText: string): ON_BILLING_STATUS | null => {
  const lower = statusText.toLowerCase();
  if (lower.includes('billing issue')) return ON_BILLING_STATUS.BILLING_ISSUE;
  if (lower === 'dispatch processing')
    return ON_BILLING_STATUS.DISPATCH_PROCESSING;
  if (lower === 'dispatch verified') return ON_BILLING_STATUS.DISPATCH_VERIFIED;
  if (lower === 'invoiced') return ON_BILLING_STATUS.INVOICED;
  return null;
};

const LoadStatusAppBar: React.FC<Props> = props => {
  const classes = useStyles(props);
  const {
    flex,
    loadStatus,
    onTimeStatus,
    sticky,
    loadId,
    status: currentStatus,
    onStatusUpdated,
  } = props;

  const { rootStore } = useStores();
  const user = rootStore.me?.maybeCurrent;
  const isDispatcher = !!user?.isDispatcher;
  const isBilling = !!user?.isBilling;
  const isInvoiced = currentStatus === LOAD_STATUS.INVOICED;

  const isCanEdit =
    (isDispatcher || isBilling) && !!loadId && !!currentStatus && !isInvoiced;

  const billingStatus = getBillingStatusEnum(loadStatus);
  const displayMessage =
    billingStatus !== null || onTimeStatus === ON_TIME_STATUS.UNKNOWN
      ? loadStatus
      : ON_TIME_STATUS_TEXT[onTimeStatus];

  return (
    <AppBar
      position="static"
      className={[classes.appBar, sticky ? classes.sticky : ''].join(' ')}
      style={{ flex }}
    >
      <Toolbar className={classes.toolbar} variant="dense">
        {isCanEdit ? (
          <EditableStatusSelect
            loadId={loadId!}
            currentStatus={currentStatus!}
            displayMessage={displayMessage}
            isBillingUser={isBilling}
            onStatusUpdated={onStatusUpdated}
          />
        ) : (
          <ReadOnlyStatusText displayMessage={displayMessage} />
        )}
      </Toolbar>
    </AppBar>
  );
};

const useStyles = makeStyles(theme => ({
  appBar: (props: Props) => {
    const billingStatus = getBillingStatusEnum(props.loadStatus);
    const bgColor =
      billingStatus !== null
        ? onBillingStatusColors(theme)[billingStatus]
        : onTimeStatusColors(theme)[props.onTimeStatus];

    return {
      backgroundColor: bgColor,
      color: theme.palette.getContrastText(bgColor),
    };
  },
  sticky: { position: 'sticky', top: 0 },
  toolbar: {
    margin: 'auto',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    width: '100%',
  },
}));

export default React.memo(observer(LoadStatusAppBar));
